import { z } from 'zod';
import {
  ADD_YOUR_SCRIPT_PROMPT_USED_IN,
  PERSONA_REFERENCE_VIDEO_ANALYSIS_DEFAULT_MODEL,
  PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN,
  PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN,
} from '../events/persona-opportunities.js';
import { CHANGELOG_PROMPT_DEPLOY_NOTES, CHANGELOG_PROMPT_ROUNDUP } from './changelog-prompts.js';
import {
  SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
  SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
  SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
  SCRIPT_REVIEW_PROMPT_SUGGESTION,
  SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
  SCRIPT_REVIEW_PROMPT_VISUAL,
  SCRIPT_REVIEW_PROMPT_VOICEOVER,
} from './script-review-prompts.js';

export const PromptServiceDefaultSourceSchema = z.object({
  /**
   * Env var on the shared ConfigMap (e.g. ANTHROPIC_MODEL).
   * Omit when the worker uses a fixed package default and reads no env key.
   */
  configKey: z.string().min(1).optional(),
  /** Package default when the worker reads config with no override. */
  packageDefault: z.string().min(1),
  /** Read when `configKey` is unset. Changelog uses USERS_CHANGELOG_MODEL, then GEMINI_MODEL. */
  fallbackConfigKey: z.string().min(1).optional(),
});
export type PromptServiceDefaultSource = z.infer<typeof PromptServiceDefaultSourceSchema>;

export const PromptServiceDefaultModelSchema = z.object({
  /** Env key that supplied `resolved`. Null when the runtime uses a fixed package default. */
  configKey: z.string().min(1).nullable(),
  /** Resolved model id from the API environment, or null when unset. */
  resolved: z.string().min(1).nullable(),
});
export type PromptServiceDefaultModel = z.infer<typeof PromptServiceDefaultModelSchema>;

const ANTHROPIC = { configKey: 'ANTHROPIC_MODEL', packageDefault: 'claude-opus-5-5' } as const;
const GEMINI_TEXT = { configKey: 'GEMINI_MODEL', packageDefault: 'gemini-3.8-flash' } as const;
const PREFLIGHT_REVIEW = {
  configKey: 'PREFLIGHT_SCRIPT_REVIEW_MODEL',
  packageDefault: 'gemini-3.8-flash',
} as const;
const PERSONA_AVATAR = {
  configKey: 'PERSONAS_AVATAR_IMAGE_MODEL',
  packageDefault: 'gemini-3-pro-image',
} as const;
const OPPORTUNITIES_SET_GEN = {
  configKey: 'OPPORTUNITIES_ENGINE_SET_GEN_MODEL',
  packageDefault: 'gemini-3.1-pro-preview',
} as const;
const STORYBOARD_BEAT = {
  configKey: 'OPPORTUNITIES_ENGINE_STORYBOARD_BEAT_MODEL',
  packageDefault: 'gemini-3.1-pro-preview',
} as const;
const STORYBOARD_IMAGE = {
  configKey: 'OPPORTUNITIES_ENGINE_STORYBOARD_IMAGE_MODELS',
  packageDefault: 'gemini-3.1-flash-lite-image',
} as const;
/** Worker always uses the package constant; no env override exists. */
const PERSONA_REF_VIDEO = {
  packageDefault: PERSONA_REFERENCE_VIDEO_ANALYSIS_DEFAULT_MODEL,
} as const;
const CHANGELOG = {
  configKey: 'USERS_CHANGELOG_MODEL',
  fallbackConfigKey: 'GEMINI_MODEL',
  packageDefault: 'gemini-3.8-flash',
} as const;

/**
 * Which runtime config key backs "Service default" for each Prompt Manager usedIn.
 * Keys not listed have no documented service default (UI shows generic copy only).
 */
export const PROMPT_SERVICE_DEFAULT_SOURCE: Readonly<
  Record<string, PromptServiceDefaultSource>
> = {
  'opportunity-generation-premise': OPPORTUNITIES_SET_GEN,
  'opportunity-generation-framework': OPPORTUNITIES_SET_GEN,
  'creative-script-generation-angles': ANTHROPIC,
  'creative-script-generation-topics': ANTHROPIC,
  'creative-script-generation-custom': ANTHROPIC,
  [ADD_YOUR_SCRIPT_PROMPT_USED_IN]: ANTHROPIC,
  'creative-script-generation-persona-angles': ANTHROPIC,
  'creative-brief-generation-persona-angles': ANTHROPIC,
  [PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN]: ANTHROPIC,
  'creative-brief-edit-persona-angles': ANTHROPIC,
  'creative-script-hook-variations-persona': ANTHROPIC,
  'creative-script-edit-meta-prompt': ANTHROPIC,
  'creative-script-edit-feedback-message': ANTHROPIC,
  'creative-storyboard-beat-extraction': STORYBOARD_BEAT,
  'creative-storyboard-image': STORYBOARD_IMAGE,
  'persona-avatar-generation': PERSONA_AVATAR,
  [PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN]: PERSONA_REF_VIDEO,
  [SCRIPT_REVIEW_PROMPT_TRANSCRIPT]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_VOICEOVER]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_VISUAL]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_SUGGESTION]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY]: PREFLIGHT_REVIEW,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL]: PREFLIGHT_REVIEW,
  [CHANGELOG_PROMPT_DEPLOY_NOTES]: CHANGELOG,
  [CHANGELOG_PROMPT_ROUNDUP]: CHANGELOG,
};

export function getPromptServiceDefaultSource(usedIn: string): PromptServiceDefaultSource | null {
  return PROMPT_SERVICE_DEFAULT_SOURCE[usedIn] ?? null;
}

function envModelForConfigKey(
  configKey: string,
  raw: string | undefined,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  if (configKey === 'OPPORTUNITIES_ENGINE_STORYBOARD_IMAGE_MODELS') {
    const models = trimmed
      .split(',')
      .map((model) => model.trim())
      .filter((model) => model.length > 0);
    return models.length > 0 ? models.join(', ') : null;
  }
  return trimmed;
}

function resolvedFromKey(
  configKey: string,
  envByConfigKey: Readonly<Record<string, string | undefined>>,
): PromptServiceDefaultModel {
  return {
    configKey,
    resolved: envModelForConfigKey(configKey, envByConfigKey[configKey]),
  };
}

/** Resolve the model id the API can read from env, or the fixed package default. */
export function resolvePromptServiceDefaultModel(
  usedIn: string,
  envByConfigKey: Readonly<Record<string, string | undefined>>,
): PromptServiceDefaultModel | null {
  const source = getPromptServiceDefaultSource(usedIn);
  if (!source) return null;
  if (!source.configKey) {
    return { configKey: null, resolved: source.packageDefault };
  }

  const primary = resolvedFromKey(source.configKey, envByConfigKey);
  if (primary.resolved || !source.fallbackConfigKey) return primary;
  return resolvedFromKey(source.fallbackConfigKey, envByConfigKey);
}
